/**
 * S-91 第2弾: 素リンク SSO 遷移の自己修復（bounce）。
 *
 * `onNavigateApp` は「一度 SSO を確立した app」へトークンなしの素リンクで遷移するが、
 * 遷移先のセッションが失効していると AuthGuard が signed-out を検出する。その場合、
 * サインイン画面に落とす代わりに portal へ `?sso_retry=<feature>` で戻し、portal が
 * トークン付きハンドオーバーを自動でやり直す（ユーザー操作なしで復帰）。
 *
 * 発動条件（全て満たす場合のみ）:
 * - referrer が許可オリジン（portal / 他の管理 app / 旧オリジン）からの遷移である
 *   （ブックマーク・直リンクの signed-out は従来どおり /signin へ）
 * - 直近 60 秒以内に bounce していない（発行やサインインが恒常的に失敗している場合の
 *   portal ⇄ app 無限ループ防止）
 * - prerender 中でない（prerender 中の redirect は破棄されるため activate 後に実行）
 *
 * なお「ページ滞在中のユーザー自身のサインアウト」と「素リンク到着時のセッション失効」の
 * 区別は本関数では行わない（referrer だけでは判別不能）。呼出側の `useSsoRetryBounce` が
 * 「到着時から signed-out だったか」を判定して本関数を呼ぶかどうかを決める。
 */
const BOUNCED_AT_KEY = 'kawai:sso-bounced-at';
const BOUNCE_THROTTLE_MS = 60 * 1000;
function getDefaultSessionStorage() {
    try {
        return window.sessionStorage;
    }
    catch {
        return null;
    }
}
/**
 * 条件を満たす場合 portal へ redirect を開始して true を返す（呼出側は繋ぎ表示を出す）。
 * 満たさない場合は false（呼出側は従来どおり /signin へ）。
 */
export function maybeRedirectForSsoRetry(opts) {
    const referrer = opts.referrer ?? (typeof document !== 'undefined' ? document.referrer : '');
    const storage = opts.storage === undefined ? getDefaultSessionStorage() : opts.storage;
    const now = opts.now ?? Date.now;
    const navigate = opts.navigate ??
        ((url) => {
            window.location.href = url;
        });
    const isPrerendering = opts.isPrerendering ??
        (() => typeof document !== 'undefined' &&
            document.prerendering === true);
    const addPrerenderingChangeListener = opts.addPrerenderingChangeListener ??
        ((fn) => {
            document.addEventListener('prerenderingchange', fn, { once: true });
        });
    // referrer 判定: 許可オリジンからの遷移のみ bounce する。
    let referrerOrigin;
    try {
        referrerOrigin = new URL(referrer).origin;
    }
    catch {
        return false;
    }
    const allowed = opts.allowedReferrerOrigins.some((o) => {
        try {
            return new URL(o).origin === referrerOrigin;
        }
        catch {
            return false;
        }
    });
    if (!allowed)
        return false;
    // ループ防止: 直近の bounce から 60 秒間は再発動しない。
    // sessionStorage 不能ならループ防止が効かないため bounce しない（安全側）。
    if (!storage)
        return false;
    try {
        const last = Number(storage.getItem(BOUNCED_AT_KEY) ?? '0');
        if (now() - last < BOUNCE_THROTTLE_MS)
            return false;
        storage.setItem(BOUNCED_AT_KEY, String(now()));
    }
    catch {
        return false;
    }
    const url = new URL(opts.portalUrl);
    url.searchParams.set('sso_retry', opts.feature);
    const redirect = () => navigate(url.toString());
    if (isPrerendering()) {
        // prerender 中の redirect は activate で破棄される。activate 後に実行する。
        addPrerenderingChangeListener(redirect);
        return true;
    }
    redirect();
    return true;
}
//# sourceMappingURL=ssoRetryRedirect.js.map